import api from "../lib/axios";
import environment from "../environments/environment";

const RSVP_FLOW_BASE = "/rsvp/flow";

const authHeaders = (token) => ({
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  "ngrok-skip-browser-warning": "true",
});

export const getRsvpFlows = (eventId) => api.get(`${RSVP_FLOW_BASE}/event/${eventId}/flows`);

export const createRsvpFlow = (eventId, body) =>
  api.post(`${RSVP_FLOW_BASE}/event/${eventId}/flows`, body);

export const duplicateRsvpFlow = (eventId, flowId) =>
  api.post(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/duplicate`);

export const updateRsvpFlow = (eventId, flowId, body) =>
  api.put(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}`, body);

export const deleteRsvpFlow = (eventId, flowId) =>
  api.delete(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}`);

export const getRsvpAudience = (eventId, flowId) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/audience`);

export const saveRsvpAudience = (eventId, flowId, body) =>
  api.put(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/audience`, body);

export const getRsvpBroadcastAudienceOptions = (eventId, params = {}, config = {}) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/broadcast-audience-options`, { ...config, params });

export const getRsvpTriggers = (eventId, flowId) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/triggers`);

export const saveRsvpTriggers = (eventId, flowId, triggers) =>
  api.put(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/triggers`, { triggers });

export const getRsvpWorkflow = (eventId, flowId) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/workflow`);

export const saveRsvpWorkflow = (eventId, flowId, body) =>
  api.put(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/workflow`, body);

export const getRsvpRecipients = (eventId, flowId) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/recipients`);

export const createRsvpRecipient = (eventId, flowId, body) =>
  api.post(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/recipients`, body);

export const createRsvpRegisteredMembers = (eventId, flowId, body) =>
  api.post(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/registered-members`, body);

export const deleteRsvpRecipient = (eventId, recipientId) =>
  api.delete(`${RSVP_FLOW_BASE}/event/${eventId}/recipients/${recipientId}`);

export const createRsvpImport = (eventId, flowId, body) =>
  api.post(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/imports`, body);

export const createRsvpImportRows = (importId, rows) =>
  api.post(`${RSVP_FLOW_BASE}/imports/${importId}/rows`, { rows });

export const getRsvpSessions = (eventId, flowId) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/sessions`);

export const getRsvpEventSessions = (eventId, params = {}) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/sessions`, { params });

export const getRsvpSessionMessages = (sessionId) =>
  api.get(`${RSVP_FLOW_BASE}/sessions/${sessionId}/messages`);

export const markRsvpSessionAsRead = (sessionId) =>
  api.put(`${RSVP_FLOW_BASE}/sessions/${sessionId}/read`);

export const createRsvpMessage = (eventId, flowId, sessionId, body) => {
  const url = flowId && Number(flowId) > 0
    ? `${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/sessions/${sessionId}/messages`
    : `${RSVP_FLOW_BASE}/sessions/${sessionId}/messages`;
  return api.post(url, { ...body, event_id: eventId, communication_flow_id: flowId || undefined });
};

export const getRsvpBroadcasts = (eventId, flowId) =>
  api.get(
    flowId
      ? `${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/broadcasts`
      : `${RSVP_FLOW_BASE}/event/${eventId}/broadcasts`,
  );

export const getRsvpBroadcastStatusStreamUrl = (eventId) =>
  `${environment.apiUrl}${RSVP_FLOW_BASE}/event/${eventId}/broadcasts/status-stream?ngrok-skip-browser-warning=true`;

export const createRsvpBroadcast = (eventId, flowId, body) =>
  api.post(`${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/broadcasts`, body);

export const createRsvpBroadcastRecipients = (broadcastId, rows) =>
  api.post(`${RSVP_FLOW_BASE}/broadcasts/${broadcastId}/recipients`, { rows });

export const testRsvpWaTemplate = (eventId, templateId, body) =>
  api.post(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates/${templateId}/test`, body);

export const getRsvpLogs = (eventId, flowId) =>
  api.get(
    flowId
      ? `${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/logs`
      : `${RSVP_FLOW_BASE}/event/${eventId}/logs`,
  );

export const getRsvpFlowAnalytics = (eventId, flowId) =>
  api.get(
    flowId
      ? `${RSVP_FLOW_BASE}/event/${eventId}/flows/${flowId}/analytics`
      : `${RSVP_FLOW_BASE}/event/${eventId}/analytics`,
  );

export const getRsvpTemplates = (eventId, channel) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/templates${channel ? `?channel=${channel}` : ""}`);

export const createRsvpTemplate = (eventId, body) =>
  api.post(`${RSVP_FLOW_BASE}/event/${eventId}/templates`, body);

export const getRsvpWaTemplates = (eventId) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates`);

export const getWaTemplateButtonUrlPreview = (eventId, params) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates/button-url-preview`, { params });

export const checkRsvpWaTemplateName = (eventId, name) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates/check-name`, {
    params: { name },
  });

export const getWaTemplateDefaultTypes = (eventId) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/wa-template-default-types`);

export const setWaTemplateDefaults = (eventId, templateId, typeId, scope = "event") =>
  api.put(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates/${templateId}/defaults`, { typeId, scope });

export const setWaTemplateEnabled = (eventId, templateId, isEnabled) =>
  api.put(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates/${templateId}/enabled`, { is_enabled: isEnabled });

const buildWaTemplateRequestBody = (body = {}) => {
  if (!body?.headerMediaFile) {
    return body;
  }

  const formData = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (key === "headerMediaFile") {
      formData.append("link", value);
    } else if (key === "headerMediaUrl") {
      return;
    } else if (Array.isArray(value) || (value && typeof value === "object")) {
      formData.append(key, JSON.stringify(value));
    } else if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  return formData;
};

const waTemplateRequestConfig = (body) =>
  body?.headerMediaFile
    ? {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    : undefined;

export const createRsvpWaTemplate = (eventId, body) =>
  api.post(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates`, buildWaTemplateRequestBody(body), waTemplateRequestConfig(body));

export const importRsvpWaTemplateById = (eventId, body) =>
  api.post(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates/import`, body);

export const lookupRsvpWaTemplateById = (eventId, templateId) =>
  api.get(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates/lookup`, {
    params: { whatsapp_template_id: templateId },
  });

export const uploadRsvpWaTemplateMedia = (eventId, file, token) => {
  const formData = new FormData();
  formData.append("file", file);

  return api.post(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates/media`, formData, {
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });
};

export const updateRsvpWaTemplate = (eventId, templateId, body) =>
  api.put(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates/${templateId}`, buildWaTemplateRequestBody(body), waTemplateRequestConfig(body));

export const deleteRsvpWaTemplate = (eventId, templateId) =>
  api.delete(`${RSVP_FLOW_BASE}/event/${eventId}/wa-templates/${templateId}`);

export const getHotels = (eventId, params = {}, token) =>
  api.get(`/rsvp/hotels`, {
    params: { event_id: eventId, ...params },
    headers: authHeaders(token),
  });

export const getHotel = (id, eventId, token) =>
  api.get(`/rsvp/hotels/${id}`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
  });

export const getHotelAllotments = (id, eventId, token) =>
  api.get(`/rsvp/hotels/${id}/allotments`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
  });

export const createHotel = (eventId, payload, token) =>
  api.post(`/rsvp/hotels`, { ...payload, event_id: eventId }, { headers: authHeaders(token) });

export const updateHotel = (id, eventId, payload, token) =>
  api.put(`/rsvp/hotels/${id}`, { ...payload, event_id: eventId }, { headers: authHeaders(token) });

export const deleteHotel = (id, eventId, token) =>
  api.delete(`/rsvp/hotels/${id}`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
  });

export const uploadHotelImage = (eventId, file, token) => {
  const formData = new FormData();
  formData.append("image", file);
  formData.append("event_id", eventId);
  return api.post(`/rsvp/hotels/image`, formData, {
    params: { event_id: eventId },
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getAccommodationAttendees = (eventId, params = {}, token) =>
  api.get(`/rsvp/accommodation/attendees`, {
    params: { event_id: eventId, ...params },
    headers: authHeaders(token),
  });

export const getAccommodationSummary = (eventId, token) =>
  api.get(`/rsvp/accommodation/summary`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
  });

export const assignHotel = (submissionId, eventId, payload, token) =>
  api.put(
    `/rsvp/accommodation/assign/${submissionId}`,
    { ...payload, event_id: eventId },
    { headers: authHeaders(token) },
  );

export const assignGroupHotel = (eventId, payload, token) =>
  api.put(
    `/rsvp/accommodation/assign-group`,
    { ...payload, event_id: eventId },
    { headers: authHeaders(token) },
  );

export const updateAccommodationStatus = (accessId, eventId, status, token) =>
  api.put(
    `/rsvp/accommodation/status/${accessId}`,
    { event_id: eventId, status },
    { headers: authHeaders(token) },
  );

export const resendAccommodationNotifications = (eventId, payload, token) =>
  api.post(
    `/rsvp/accommodation/resend-notifications`,
    { ...payload, event_id: eventId },
    { headers: authHeaders(token) },
  );

export const getAccommodationResendStatus = (eventId, jobId, token) =>
  api.get(`/rsvp/accommodation/resend-notifications/status/${jobId}`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
  });

export const downloadAccommodationImportTemplate = (eventId, token) =>
  api.get(`/rsvp/accommodation/template`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
    responseType: "blob",
  });

export const importAccommodation = (eventId, formData, token) =>
  api.post(`/rsvp/accommodation/import`, formData, {
    params: { event_id: eventId },
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });

export const getVehicles = (eventId, params = {}, token) =>
  api.get(`/rsvp/vehicles`, {
    params: { event_id: eventId, ...params },
    headers: authHeaders(token),
  });

export const getVehicle = (id, eventId, token) =>
  api.get(`/rsvp/vehicles/${id}`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
  });

export const createVehicle = (eventId, payload, token) =>
  api.post(`/rsvp/vehicles`, { ...payload, event_id: eventId }, { headers: authHeaders(token) });

export const updateVehicle = (id, eventId, payload, token) =>
  api.put(`/rsvp/vehicles/${id}`, { ...payload, event_id: eventId }, { headers: authHeaders(token) });

export const deleteVehicle = (id, eventId, token) =>
  api.delete(`/rsvp/vehicles/${id}`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
  });

export const uploadVehicleImage = (eventId, file, token) => {
  const formData = new FormData();
  formData.append("image", file);
  formData.append("event_id", eventId);
  return api.post(`/rsvp/vehicles/image`, formData, {
    params: { event_id: eventId },
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getTransportCompanies = (eventId, token) =>
  api.get(`/rsvp/transport-companies`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
  });

export const getTransportAttendees = (eventId, params = {}, token) =>
  api.get(`/rsvp/transportation/attendees`, {
    params: { event_id: eventId, ...params },
    headers: authHeaders(token),
  });

export const getTransportSummary = (eventId, token) =>
  api.get(`/rsvp/transportation/summary`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
  });

export const assignVehicle = (submissionId, eventId, payload, token) =>
  api.put(
    `/rsvp/transportation/assign/${submissionId}`,
    { ...payload, event_id: eventId },
    { headers: authHeaders(token) },
  );

export const assignGroupVehicle = (eventId, payload, token) =>
  api.put(
    `/rsvp/transportation/assign-group`,
    { ...payload, event_id: eventId },
    { headers: authHeaders(token) },
  );

export const updateTransportStatus = (accessId, eventId, status, token) =>
  api.put(
    `/rsvp/transportation/status/${accessId}`,
    { event_id: eventId, status },
    { headers: authHeaders(token) },
  );

export const resendTransportNotifications = (eventId, payload, token) =>
  api.post(
    `/rsvp/transportation/resend-notifications`,
    { ...payload, event_id: eventId },
    { headers: authHeaders(token) },
  );

export const getTransportResendStatus = (eventId, jobId, token) =>
  api.get(`/rsvp/transportation/resend-notifications/status/${jobId}`, {
    params: { event_id: eventId },
    headers: authHeaders(token),
  });

/** SSE stream of the latest resend job for an event. scope: accommodation | transportation */
export const getRsvpResendStatusStreamUrl = (eventId, scope, jobId) => {
  const params = new URLSearchParams({
    event_id: String(eventId),
    "ngrok-skip-browser-warning": "true",
  });
  if (jobId) params.set("job_id", String(jobId));
  return `${environment.apiUrl}/rsvp/${scope}/resend-notifications/status-stream?${params.toString()}`;
};

export const getRsvpAnalytics = (eventId, params = {}, token) =>
  api.get(`/rsvp/analytics`, {
    params: { event_id: eventId, ...params },
    headers: authHeaders(token),
  });

export const downloadRsvpImportTemplate = (
  eventId,
  scope = "all",
  token,
  includeExisting = false,
  unassignedOnly = false
) =>
  api.get(`/rsvp/import-export/template`, {
    params: {
      event_id: eventId,
      scope,
      include_existing: includeExisting,
      unassigned_only: unassignedOnly,
    },
    headers: authHeaders(token),
    responseType: "blob",
  });

export const previewRsvpImport = (eventId, formData, token) =>
  api.post(`/rsvp/import-export/preview`, formData, {
    params: { event_id: eventId },
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });

export const importRsvpAllotments = (eventId, formData, token) =>
  api.post(`/rsvp/import-export/import`, formData, {
    params: { event_id: eventId },
    headers: {
      ...authHeaders(token),
      "Content-Type": "multipart/form-data",
    },
  });

export const exportRsvpAllotments = (eventId, scope = "all", token, filters = {}) =>
  api.get(`/rsvp/import-export/export`, {
    params: { event_id: eventId, scope, ...filters },
    headers: authHeaders(token),
    responseType: "blob",
  });

// RSVP Allotments (Accommodation & Transportation)
export const getAccommodationMatrix = (eventId, hotelId = null, token) =>
  api.get(`/rsvp/allotment/accommodation-matrix`, {
    params: { event_id: eventId, hotel_id: hotelId || undefined },
    headers: authHeaders(token),
  });

export const saveAccommodationAllotments = (eventId, hotelId, allotments, options = {}, token) =>
  api.post(
    `/rsvp/allotment/save-accommodation`,
    {
      event_id: eventId,
      hotel_id: hotelId,
      allotments,
      options,
      send_whatsapp: options.send_whatsapp || false,
      send_email: options.send_email || false,
    },
    { headers: authHeaders(token) }
  );

export const getTransportMatrix = (eventId, token, filters = {}) =>
  api.get(`/rsvp/allotment/transport-matrix`, {
    params: { event_id: eventId, ...filters },
    headers: authHeaders(token),
  });

export const saveTransportAllotments = (eventId, allotments, options = {}, token) =>
  api.post(
    `/rsvp/allotment/save-transport`,
    { event_id: eventId, allotments, options },
    { headers: authHeaders(token) }
  );

export const getAllotmentAttendees = (eventId, scope = "all", token, options = {}) =>
  api.get(`/rsvp/allotment/attendees`, {
    params: { event_id: eventId, scope, ...options },
    headers: authHeaders(token),
  });
